import { useLoaderData } from "react-router";
import { DataSourceBadge, DataSourceNotice } from "@/components/layout/data-source-notice";
import { PageHeader } from "@/components/layout/page-header";
import { Badge, EmptyState, Panel, PanelHeader, type Tone } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { formatDateTime } from "@/lib/format";
import type { GeneratedReport, PortfolioStat, ReportSummary } from "@/lib/types";
import type { ApiFailure } from "@/services/api-client";
import { getPortfolioStats, listGeneratedReports, listReports } from "@/services/reports";

interface ReportsData {
  generated: GeneratedReport[];
  fallback: ApiFailure | null;
  catalogue: ReportSummary[];
  stats: PortfolioStat[];
}

export async function reportsLoader(): Promise<ReportsData> {
  const [{ data: generated, fallback }, catalogue, stats] = await Promise.all([
    listGeneratedReports(),
    listReports(),
    getPortfolioStats(),
  ]);
  return { generated, fallback, catalogue, stats };
}

function statusTone(status: string): Tone {
  if (status === "ready" || status === "completed") return "positive";
  if (status === "failed" || status === "error") return "negative";
  return "caution";
}

export function ReportsPage() {
  useDocumentTitle("Reports");
  const { generated, fallback, catalogue, stats } = useLoaderData<ReportsData>();

  return (
    <>
      <PageHeader
        title="Reports"
        description="Portfolio-level views. Case work happens in the inbox."
        action={<DataSourceBadge fallback={fallback} />}
      />

      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Panel>
          <dl className="grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
            {stats.map((stat) => (
              <div key={stat.id} className="px-4 py-3">
                <dt className="eyebrow">{stat.label}</dt>
                <dd className="numeric mt-1 text-lg font-semibold text-ink">{stat.value}</dd>
                {stat.delta ? (
                  <dd className="numeric mt-0.5 text-xs text-ink-muted">{stat.delta}</dd>
                ) : null}
              </div>
            ))}
          </dl>
        </Panel>

        <Panel>
          <PanelHeader
            title="Generated reports"
            description="Reports this workspace has produced."
          />
          {fallback ? (
            <div className="px-4 py-3">
              <DataSourceNotice fallback={fallback} />
            </div>
          ) : generated.length === 0 ? (
            <EmptyState
              title="Nothing generated yet"
              detail="Reports produced by the platform will appear here."
            />
          ) : (
            <ul className="divide-y divide-line">
              {generated.map((report) => (
                <GeneratedRow key={report.id} report={report} />
              ))}
            </ul>
          )}
        </Panel>

        <Panel>
          <PanelHeader
            title="Available reports"
            description="The catalogue of views the platform offers. Not yet backed by the API."
          />
          <ul className="divide-y divide-line">
            {catalogue.map((report) => (
              <li
                key={report.id}
                className="flex flex-wrap items-start justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0 max-w-xl">
                  <p className="text-sm font-medium text-ink">{report.name}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
                    {report.description}
                  </p>
                </div>
                <div className="text-right text-xs text-ink-faint">
                  <div>{report.cadence}</div>
                  <div className="numeric mt-0.5">Last run {formatDateTime(report.lastRun)}</div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}

function GeneratedRow({ report }: { report: GeneratedReport }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink">{report.title}</p>
        <p className="numeric mt-0.5 text-xs text-ink-muted">
          {report.recordCount.toLocaleString("en-US")} rows · {report.format.toUpperCase()} ·
          created {formatDateTime(report.createdAt)}
        </p>
        {report.errorCode ? (
          <p className="mt-0.5 text-xs text-negative">Failed: {report.errorCode}</p>
        ) : null}
      </div>
      <Badge tone={statusTone(report.status)}>{report.status}</Badge>
    </li>
  );
}
